import { Avatar } from "@mui/material";
import moment from "moment";
import React, { useEffect, useRef } from "react";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { POUND_SIGN } from "@/Constants/common";
import { routingConstants } from "@/Constants/routingConstants";
import {
  fetchClientInvoices,
  updateSelectedInvoices,
  updateToggleSelected,
  toggleSelectedAll,
  setLoading,
  updatePagination,
  deSelectAllInvoices,
  updateSorting,
} from "@/reducers/clientinvoice";
import { getColumnNormalHtml } from "@/utilities/datatable/columnFunctions";
import useDebounceScroll from "@/utilities/hooks/useDebounceScroll";

import Actions from "./Actions";
import FilterModal from "./FilterModal";
import SearchFilter from "./SearchFilter";
import { TOOLTIPTEXT } from "../../../../utilities/CommonText";
import { showSuccessToast } from "../../../../utilities/utils";
import DataTablePaginationLoader from "../../../Skeletons/DataTablePaginationLoader";
import TableLoader from "../../TableLoader";
import TableNoContent from "../../TableNoContent";
import { SelectedCountText } from "../candidates/Listing/common/SelectedCountText";
import CandidateStatusAndInfo from "../dataTables/CandidateStatusAndInfo";
/**
 * Functional component for rendering the list of invoices.
 *
 * @return {JSX.Element} The list of invoices.
 */
const InvoiceList = () => {
  const {
    selectedInvoices,
    isLoading,
    clientInvoices,
    toggleSelected,
    totalCount,
    pagination,
    isSelectedAll,
    totalAmt,
    isPaginationLoading,
    sorting,
  } = useSelector((state) => state.clientInvoices);
  const { clientUserAllPermissions } = useSelector((state) => state.session);
  const dispatch = useDispatch();
  useEffect(() => {
    if (selectedInvoices.length == 0) {
      dispatch(updateToggleSelected());
    }

    if (localStorage.getItem("flag")) {
      showSuccessToast(TOOLTIPTEXT?.downloadingInvoice);
      setTimeout(() => {
        localStorage.removeItem("flag");
      }, 2000);
    }
  }, [selectedInvoices]);
  const columns = [
    {
      name: "Candidate",
      sortable: true,
      sortField: "candidate_forename",
      /**
       * Renders a cell with the candidate information.
       *
       * @param {Object} row - the row data for the candidate
       * @return {JSX.Element} the rendered cell component
       */
      selector: (row) => (
        <div className="flex gap-[14px]">
          <div className="min-w-[45px]">
            <Avatar
              alt={row.candidate_name}
              sx={{ width: 45, height: 45 }}
              src={row.profile_image ? row.profile_image : ""}
            />
          </div>
          <div className="flex grow flex-col gap-1 overflow-hidden">
            {(!clientUserAllPermissions ||
              clientUserAllPermissions.is_allow_to_view_candidate_profile ===
                1) &&
            row.is_past_booking ? (
              <>
                <Link
                  to={routingConstants.candidateDetail(row.route_id)}
                  className="truncate text-[14px] font-semibold leading-[normal] text-black"
                >
                  {row.candidate_name}
                </Link>
              </>
            ) : (
              <span className="cursor-not-allowed truncate text-[14px] font-semibold leading-[normal] text-black">
                {row.candidate_name}
              </span>
            )}
            <CandidateStatusAndInfo
              sortlisted={row.candidate_sortlisted}
              unsuitable={row.candidate_unsuitable}
              is_auto_booked_on_apply={row.is_auto_booked_on_apply}
              extraEle={
                <span className="line-clamp-1 text-[12px] font-medium leading-[normal] text-txtColor">
                  {row.invoice_ref_no}
                </span>
              }
            />
          </div>
        </div>
      ),
      width: "20%",
    },
    {
      name: "Contract Ref No",
      sortable: true,
      sortField: "contract_ref_no",

      /**
       * Renders a cell with the candidate information.
       *
       * @param {Object} row - the row data for the candidate
       * @return {JSX.Element} the rendered cell component
       */
      selector: (row) => (
        <div className="flex flex-col gap-1">
          {!clientUserAllPermissions ||
          clientUserAllPermissions.is_allow_to_view_contract_detail === 1 ? (
            <>
              <Link
                to={routingConstants.contractDetail(row.route_contract_id)}
                className="line-clamp-1 text-[12px] font-semibold leading-[normal] text-txtColor"
              >
                {row.contract_ref_no}
              </Link>
              {getColumnNormalHtml(
                row.job_title,
                "line-clamp-2 whitespace-pre-wrap",
              )}
            </>
          ) : (
            <>
              <span className="line-clamp-1 cursor-default text-[12px] font-semibold leading-[normal] text-txtColor">
                {row.contract_ref_no}
              </span>
              {getColumnNormalHtml(
                row.job_title,
                "line-clamp-2 whitespace-pre-wrap",
              )}
            </>
          )}
        </div>
      ),
      width: "18%",
    },
    {
      name: "Role",
      sortable: true,
      sortField: "role_name",
      selector: (row) => (
        <div className="flex flex-col" data-tag="allowRowEvents">
          {getColumnNormalHtml(
            row.role_name,
            "line-clamp-1 whitespace-break-spaces",
          )}
        </div>
      ),
      width: "15%",
    },
    {
      name: "Invoice Date",
      sortable: true,
      sortField: "created_at",
      selector: (row) => (
        <div className="grid gap-1">
          <div className="flex flex-col">
            {getColumnNormalHtml(
              moment(row.invoice_date, "YYYY-MM-DD HH:mm:ss").format(
                "DD/MM/YYYY",
              ),
            )}
          </div>
        </div>
      ),
      width: "13%",
    },
    {
      name: "status",
      sortable: false,
      selector: (row) => (
        <div className="flex select-none flex-col" data-tag="allowRowEvents">
          {row.invoice_status == "Pending" ? (
            <span className="h-[30px] w-[80px] rounded-lg border border-red bg-white py-[3px] text-center text-[14px] !text-red">
              Pending
            </span>
          ) : row.invoice_status == "Paid" ? (
            <span className="w-[75px] rounded-lg border border-green bg-white px-5 py-[3px] text-[14px] !text-green">
              Paid
            </span>
          ) : (
            <span className="w-[90px] rounded-lg border border-yellow bg-white px-[5px] py-[5px] text-center text-[14px] !text-yellow">
              Processing
            </span>
          )}
        </div>
      ),
      width: "190px",
    },

    {
      name: "Invoice Total",
      sortable: false,
      selector: (row) => (
        <div className="!mr-[40px] flex w-full flex-col items-end">
          {getColumnNormalHtml(row.total_hours, "line-clamp-1 mr-10")}
          {getColumnNormalHtml(`${POUND_SIGN} ${row.total_amount}`, "mr-10")}
        </div>
      ),
      width: "",
    },
  ];
  const dataTableRef = useRef();
  // Handles the table scroll event ...
  const handleDebounceScroll = useDebounceScroll(() =>
    dispatch(fetchClientInvoices({})),
  );
  useEffect(() => {
    if (dataTableRef.current) {
      const table = dataTableRef.current.querySelector("._DataTable");
      table.addEventListener("scroll", handleDebounceScroll);
      return () => {
        table.removeEventListener("scroll", handleDebounceScroll);
      };
    }
  }, [handleDebounceScroll]);

  const handleSort = ({ sortField: newSortField }) => {
    const { sortByColumn, sortByOrder } = sorting; // get from Redux

    let newSortOrder = "asc";
    if (sortByColumn === newSortField) {
      newSortOrder = sortByOrder === "asc" ? "desc" : "asc";
    }

    dispatch(
      updateSorting({ sortByColumn: newSortField, sortByOrder: newSortOrder }),
    );
    dispatch(deSelectAllInvoices());
    dispatch(
      updatePagination({
        ...pagination,
        currentPage: 1,
        isLastPage: false,
      }),
    );
    dispatch(setLoading(true));
    dispatch(fetchClientInvoices({}));
  };

  return (
    <div className="InvoiceList_Wrapper mb-[52px]">
      <div className="flex h-full flex-col rounded-[10px] bg-white p-4 shadow-[0px_x1px_6px_0px_#E7EFFB]">
        <div className="mb-[20px] flex flex-col flex-wrap gap-3 border-b border-b-[#E8E8E8] pb-[11px] md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-2 md:gap-[21px]">
            <h4 className="text-[20px] font-medium leading-[normal] text-black">
              Invoice
            </h4>
            <Actions />
            <SelectedCountText
              selectedCount={selectedInvoices.length}
              totalCount={totalCount}
              isSelectedAll={isSelectedAll}
              clearSelected={() => {
                dispatch(updateToggleSelected());
              }}
              handleSelectAll={(val) => {
                dispatch(toggleSelectedAll(val));
              }}
              updateSelected={(val) => {
                dispatch(updateSelectedInvoices(val));
              }}
              currentDataLength={clientInvoices.length}
            />
          </div>
          <div className="flex items-center gap-[20px]">
            <SearchFilter />
            <FilterModal />
          </div>
        </div>
        <div
          className="DataTable_Wrapper hide_extended_arrow"
          ref={dataTableRef}
        >
          <DataTable
            columns={columns}
            data={clientInvoices}
            fixedHeader
            onSort={handleSort}
            // persistTableHead={true}
            fixedHeaderScrollHeight="600px"
            progressPending={isLoading}
            progressComponent={<TableLoader rows={5} />}
            noDataComponent={<TableNoContent />}
            selectableRows
            onSelectedRowsChange={(state) => {
              let selectedIDS = state.selectedRows.map((item) => {
                return item.invoice_id;
              });
              dispatch(updateSelectedInvoices(selectedIDS));
            }}
            clearSelectedRows={toggleSelected}
            className="_DataTable"
          />
        </div>
        {!isLoading && isPaginationLoading ? (
          <DataTablePaginationLoader />
        ) : null}
      </div>
    </div>
  );
};

export default InvoiceList;
